import { useState, useEffect } from "react";
import { createModalCloseHandler } from "@/lib/modal-utils";
import {
  Building2,
  Users,
  DollarSign,
  Calendar,
  Mail,
  Phone,
  Edit,
  Euro,
} from "lucide-react";
import { departmentAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

export default function DepartmentDetails({
  department,
  onClose,
  onEdit,
  isOpen,
}) {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await departmentAPI.getStats(department.id);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching department stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (isOpen && department?.id) {
      fetchStats();
    }
  }, [department, isOpen]);

  // Reset stats when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStats(null);
      setLoadingStats(true);
    }
  }, [isOpen]);

  const formatCurrency = (amount) => {
    if (!amount) return "0 DH";
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "0 DH";
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
    }).format(numericAmount);
  };

  const formatDate = (date) => {
    if (!date) return "Non défini";
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(date));
  };

  return (
    <Dialog open={isOpen} onOpenChange={createModalCloseHandler(onClose)}>
      {department && (
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-8 w-8 text-blue-600" />
                <div>
                  <DialogTitle className="text-2xl">
                    {department.nom}
                  </DialogTitle>
                  <DialogDescription>
                    Détails et statistiques du département
                  </DialogDescription>
                </div>
              </div>
              <Button onClick={onEdit} variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            {/* Department Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Nom
                    </label>
                    <p className="text-lg font-medium">{department.nom}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Statut
                    </label>
                    <div className="mt-1">
                      <Badge
                        variant={department.actif ? "default" : "secondary"}
                      >
                        {department.actif ? "Actif" : "Inactif"}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Budget
                    </label>
                    <p className="text-lg font-medium">
                      {formatCurrency(department.budget)}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Date de création
                    </label>
                    <p className="text-lg font-medium">
                      {formatDate(department.date_creation)}
                    </p>
                  </div>
                </div>

                {department.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Description
                    </label>
                    <p className="mt-1 text-gray-900">
                      {department.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manager Info */}
            {department.manager && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Manager du département
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">
                        {department.manager.prenom} {department.manager.nom}
                      </h3>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {department.manager.email}
                        </div>
                        {department.manager.telephone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="h-4 w-4 mr-2" />
                            {department.manager.telephone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Statistics */}
            {!loadingStats && stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Statistiques</CardTitle>
                  <CardDescription>
                    Aperçu des données du département
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-600">
                            Total employés
                          </p>
                          <p className="text-2xl font-bold text-blue-900">
                            {stats?.total_employees || 0}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-blue-500" />
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">
                            Employés actifs
                          </p>
                          <p className="text-2xl font-bold text-green-900">
                            {stats?.active_employees || 0}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-green-500" />
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-600">
                            Nouvelles recrues
                          </p>
                          <p className="text-sm text-gray-500">
                            (3 derniers mois)
                          </p>
                          <p className="text-2xl font-bold text-orange-900">
                            {stats?.recent_hires || 0}
                          </p>
                        </div>
                        <Calendar className="h-8 w-8 text-orange-500" />
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-600">
                            Salaire moyen
                          </p>
                          <p className="text-2xl font-bold text-purple-900">
                            {stats?.average_salary
                              ? formatCurrency(stats.average_salary)
                              : "0 DH"}
                          </p>
                        </div>
                        <Euro className="h-8 w-8 text-purple-500" />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Masse salariale totale
                      </label>
                      <p className="text-xl font-semibold">
                        {stats?.total_salary
                          ? formatCurrency(stats.total_salary)
                          : "0 DH"}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Employés inactifs
                      </label>
                      <p className="text-xl font-semibold">
                        {stats?.inactive_employees || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employees List */}
            {department.employes && department.employes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Employés du département
                  </CardTitle>
                  <CardDescription>
                    Liste des employés actifs dans ce département
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {department.employes.map((employee) => (
                      <div
                        key={employee.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="bg-gray-100 rounded-full p-2">
                            <Users className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {employee.prenom} {employee.nom}
                            </p>
                            <p className="text-sm text-gray-600">
                              {employee.email}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline">
                          {employee.statut_employe}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading Stats */}
            {loadingStats && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Chargement des statistiques...</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
